import {useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {getChatUnreadStates} from 'llm/chat-library/ChatDAO';
import {subscribeChatMarkedRead} from 'llm/chat-library/ChatReadStateEvents';

export const unreadRealtimeTiming = {
  debounceMs: 500,
};

interface UseChatUnreadStatesResult {
  isChatUnread: (chatSessionId: string | null) => boolean;
  onChatRealtimeUpdate: (chatSessionId: string) => void;
}

/**
 * Maintains unread state for every currently loaded redesigned chat row. Realtime is only a signal
 * to refetch: the server query remains authoritative because it reports unread only for committed,
 * completed assistant responses.
 */
export default function useChatUnreadStates(
  chatSessionIds: Array<string>,
  activeChatSessionId: string | null
): UseChatUnreadStatesResult {
  const [unreadByChatId, setUnreadByChatId] = useState<ReadonlyMap<string, boolean>>(() => new Map());
  const chatSessionIdsRef = useRef(chatSessionIds);
  const activeChatSessionIdRef = useRef(activeChatSessionId);
  const requestSequenceRef = useRef(0);
  const readRevisionByChatIdRef = useRef<ReadonlyMap<string, number>>(new Map());
  const realtimeRefetchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const chatSessionIdsKey = chatSessionIds.join(',');
  chatSessionIdsRef.current = chatSessionIds;
  activeChatSessionIdRef.current = activeChatSessionId;

  const refetchUnreadStates = useCallback((ids: Array<string>): Promise<void> => {
    const requestSequence = ++requestSequenceRef.current;
    const readRevisionAtRequestByChatId = new Map(
      ids.map((chatSessionId) => [chatSessionId, readRevisionByChatIdRef.current.get(chatSessionId) ?? 0])
    );
    if (ids.length === 0) {
      setUnreadByChatId(new Map());
      return Promise.resolve();
    }
    return getChatUnreadStates(ids).then(
      (states) => {
        if (requestSequence !== requestSequenceRef.current) {
          return;
        }
        setUnreadByChatId(
          (current) =>
            new Map(
              states.map(({chatSessionId, isUnread}) => [
                chatSessionId,
                (readRevisionByChatIdRef.current.get(chatSessionId) ?? 0) !==
                readRevisionAtRequestByChatId.get(chatSessionId)
                  ? (current.get(chatSessionId) ?? false)
                  : isUnread,
              ])
            )
        );
      },
      () => {
        // Keep the last successful unread state through transient errors.
      }
    );
  }, []);

  useEffect(() => {
    refetchUnreadStates(chatSessionIdsRef.current);
  }, [chatSessionIdsKey, refetchUnreadStates]);

  useEffect(() => {
    if (realtimeRefetchTimeoutRef.current != null) {
      clearTimeout(realtimeRefetchTimeoutRef.current);
    }
    return () => {
      if (realtimeRefetchTimeoutRef.current != null) {
        clearTimeout(realtimeRefetchTimeoutRef.current);
      }
    };
  }, []);

  useEffect(
    () =>
      subscribeChatMarkedRead((chatSessionId) => {
        const nextReadRevisions = new Map(readRevisionByChatIdRef.current);
        nextReadRevisions.set(chatSessionId, (nextReadRevisions.get(chatSessionId) ?? 0) + 1);
        readRevisionByChatIdRef.current = nextReadRevisions;
        setUnreadByChatId((current) => {
          if (current.get(chatSessionId) !== true) {
            return current;
          }
          const next = new Map(current);
          next.set(chatSessionId, false);
          return next;
        });
      }),
    []
  );

  const onChatRealtimeUpdate = useCallback(
    (chatSessionId: string) => {
      if (chatSessionId === activeChatSessionIdRef.current) {
        return;
      }
      if (realtimeRefetchTimeoutRef.current != null) {
        clearTimeout(realtimeRefetchTimeoutRef.current);
      }
      realtimeRefetchTimeoutRef.current = setTimeout(() => {
        refetchUnreadStates(chatSessionIdsRef.current);
      }, unreadRealtimeTiming.debounceMs);
    },
    [refetchUnreadStates]
  );

  return useMemo(
    () => ({
      isChatUnread: (chatSessionId: string | null) =>
        chatSessionId != null && unreadByChatId.get(chatSessionId) === true,
      onChatRealtimeUpdate,
    }),
    [onChatRealtimeUpdate, unreadByChatId]
  );
}
